/**
 * FlowWebhooksList - Card com tabela de fluxos webhook
 * 
 * Exibe fluxos que possuem gatilho webhook em formato tabular
 * similar ao WebhookRulesList do roteamento.
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Webhook, 
  Copy, 
  Check, 
  Play, 
  Pencil, 
  Trash2,
  Activity,
  Plus,
  MessageSquare,
  Briefcase,
  Eye,
  Settings,
  MoreVertical,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { toast } from 'sonner';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';
import type { Tables } from '@/integrations/supabase/types';
import type { WebhookTriggerConfig } from '@/types/flows';

// ============= INTERFACES =============

interface FlowWebhooksListProps {
  /** Lista de fluxos com webhook */
  flows: Tables<'flows'>[];
  /** Se está carregando */
  loading: boolean;
  /** Se pode editar */
  canEdit: boolean;
  /** Se pode deletar */
  canDelete: boolean;
  /** Callback para criar novo fluxo */
  onCreateFlow: () => void;
  /** Callback para abrir modal de detalhes do webhook */
  onOpenWebhookDetail: (flow: Tables<'flows'>) => void;
  /** Callback para editar fluxo (no editor visual) */
  onEditFlow: (flow: Tables<'flows'>) => void;
  /** Callback para deletar fluxo */
  onDeleteFlow: (flow: Tables<'flows'>) => void;
  /** Callback para editar nome do fluxo */
  onEditName: (flow: Tables<'flows'>) => void;
  /** Callback para configurar gatilhos do fluxo */
  onConfigureTriggers: (flow: Tables<'flows'>) => void;
  /** Callback para duplicar fluxo */
  onDuplicate: (flow: Tables<'flows'>) => void;
  /** Callback para exportar fluxo */
  onExport?: (flow: Tables<'flows'>) => void;
  /** Callback para toggle ativo (opcional - implementação futura) */
  onToggleActive?: (flow: Tables<'flows'>) => void;
}

// ============= COMPONENTES AUXILIARES =============

/**
 * Modo do webhook — apenas ícone colorido + texto padrão (sem fundo/borda).
 */
function ModeBadge({ mode }: { mode: 'conversation' | 'deal' }) {
  if (mode === 'conversation') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
        <MessageSquare className="h-3.5 w-3.5 text-blue-600" />
        Conversa
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
      <Briefcase className="h-3.5 w-3.5 text-purple-600" />
      Negócios
    </span>
  );
}

/**
 * Status do fluxo — padrão Departamentos (Badge outline + StatusDot).
 */
function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; tone: 'emerald' | 'amber' | 'muted' }> = {
    active: { label: 'Ativo', tone: 'emerald' },
    draft: { label: 'Rascunho', tone: 'muted' },
    paused: { label: 'Pausado', tone: 'amber' },
    archived: { label: 'Arquivado', tone: 'muted' },
  };

  const cfg = config[status] || config.draft;

  return (
    <Badge
      variant="outline"
      className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
    >
      <StatusDot tone={cfg.tone} />
      {cfg.label}
    </Badge>
  );
}

// ============= COMPONENTE PRINCIPAL =============

export function FlowWebhooksList({
  flows,
  loading,
  canEdit,
  canDelete,
  onCreateFlow,
  onOpenWebhookDetail,
  onEditFlow,
  onDeleteFlow,
  onEditName,
  onConfigureTriggers,
  onDuplicate,
  onExport,
  onToggleActive,
}: FlowWebhooksListProps) {
  const navigate = useNavigate();
  
  // Estado para controlar qual URL foi copiada
  const [copiedId, setCopiedId] = useState<string | null>(null);

  /**
   * Extrai configuração de webhook do trigger_config do flow
   */
  const getWebhookConfig = (flow: Tables<'flows'>): WebhookTriggerConfig | null => {
    const triggers = flow.trigger_config as unknown as Array<{ type: string } & WebhookTriggerConfig> | null;
    if (!Array.isArray(triggers)) return null;
    const webhookTrigger = triggers.find(t => t.type === 'webhook');
    return webhookTrigger || null;
  };

  /**
   * Construir URL do webhook usando token do flow
   */
  const getWebhookUrl = (token: string) => {
    const baseUrl = getEdgeFunctionUrl('flow-webhook');
    return `${baseUrl}?token=${token}`;
  };

  /**
   * Copiar URL para clipboard
   */
  const copyUrl = async (flow: Tables<'flows'>) => {
    if (!flow.webhook_token) return;

    try {
      const url = getWebhookUrl(flow.webhook_token);
      await navigator.clipboard.writeText(url);
      setCopiedId(flow.id);
      toast.success('URL copiada para a área de transferência');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Erro ao copiar URL');
    }
  };

  /**
   * Calcular taxa de sucesso
   */
  const getSuccessRate = (flow: Tables<'flows'>): number => {
    const total = flow.webhook_total_requests || 0;
    const success = flow.webhook_successful_requests || 0;
    if (total === 0) return 0;
    return Math.round((success / total) * 100);
  };

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-md">
      {/* Gradiente decorativo no hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-500/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      <CardHeader className="relative pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Ícone em container colorido (rosa para webhook) */}
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-500/10 text-rose-600">
              <Webhook className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Fluxos com Gatilho Webhook</CardTitle>
              <CardDescription>
                {flows.length === 0
                  ? 'Nenhum fluxo com webhook configurado'
                  : `${flows.length} fluxo${flows.length > 1 ? 's' : ''} configurado${flows.length > 1 ? 's' : ''}`}
              </CardDescription>
            </div>
          </div>

          {/* Botão Novo Fluxo */}
          {canEdit && (
            <Button size="xs" onClick={onCreateFlow}>
              <Plus className="mr-1.5" />
              Novo Fluxo
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="relative pt-0">
        {flows.length === 0 ? (
          /* Estado vazio */
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 mb-4">
              <Webhook className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-1">
              Nenhum fluxo com webhook
            </h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Configure um gatilho webhook nos seus fluxos para receber eventos de sistemas externos.
            </p>
            {canEdit && (
              <Button onClick={onCreateFlow} variant="outline" size="xs" className="mt-4">
                <Plus className="mr-1.5" />
                Criar primeiro fluxo
              </Button>
            )}
          </div>
        ) : (
          /* Tabela de webhooks */
          <div className="rounded-lg border bg-background/50 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="font-semibold">Nome</TableHead>
                  <TableHead className="w-24 text-center font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Modo</TableHead>
                  <TableHead className="w-24 text-center font-semibold">Reqs</TableHead>
                  <TableHead className="w-20 text-center font-semibold">Taxa</TableHead>
                  <TableHead className="font-semibold">Endpoint</TableHead>
                  <TableHead className="w-36 text-right font-semibold">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flows.map((flow) => {
                  const webhookConfig = getWebhookConfig(flow);
                  const successRate = getSuccessRate(flow);
                  const totalReqs = flow.webhook_total_requests || 0;

                  return (
                    <TableRow
                      key={flow.id}
                      className={`cursor-pointer transition-colors ${flow.status !== 'active' ? 'opacity-60' : ''}`}
                      onClick={() => onOpenWebhookDetail(flow)}
                    >
                      {/* Nome */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Webhook className="h-4 w-4 text-rose-500" />
                          <div>
                            <div className="flex items-center gap-2 min-w-0">
                              <p className="font-medium">{flow.name}</p>
                              {/* Código identificador: 8 primeiros chars do UUID em uppercase. Selecionável. */}
                              <code
                                className="shrink-0 px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono font-semibold text-muted-foreground tracking-wider select-text cursor-text"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {flow.id.substring(0, 8).toUpperCase()}
                              </code>
                            </div>
                            {flow.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {flow.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell className="text-center">
                        <StatusBadge status={flow.status} />
                      </TableCell>

                      {/* Modo */}
                      <TableCell>
                        {webhookConfig?.mode ? (
                          <ModeBadge mode={webhookConfig.mode} />
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>

                      {/* Requisições */}
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-mono text-sm">{totalReqs}</span>
                        </div>
                      </TableCell>

                      {/* Taxa de Sucesso — apenas número%, cor pelo valor */}
                      <TableCell className="text-center">
                        <span
                          className={`text-sm font-medium ${
                            successRate >= 80
                              ? 'text-emerald-600'
                              : successRate >= 50
                              ? 'text-amber-600'
                              : 'text-red-600'
                          }`}
                        >
                          {successRate}%
                        </span>
                      </TableCell>

                      {/* Endpoint */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {flow.webhook_token ? (
                            <>
                              <code className="text-xs bg-muted px-2 py-1 rounded font-mono truncate max-w-[120px]">
                                ...{flow.webhook_token.substring(0, 12)}
                              </code>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyUrl(flow);
                                    }}
                                  >
                                    {copiedId === flow.id ? (
                                      <Check className="h-3.5 w-3.5 text-green-500" />
                                    ) : (
                                      <Copy className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {copiedId === flow.id ? 'Copiado!' : 'Copiar URL'}
                                </TooltipContent>
                              </Tooltip>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">Não configurado</span>
                          )}
                        </div>
                      </TableCell>

                      {/* Menu de Ações (3 pontinhos) */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Visualizar - sempre disponível */}
                            <DropdownMenuItem onClick={() => navigate(`/tenant/flows/${flow.id}`)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Visualizar
                            </DropdownMenuItem>
                            
                            {/* Exportar - sempre disponível */}
                            {onExport && (
                              <DropdownMenuItem onClick={() => onExport(flow)}>
                                <Download className="h-4 w-4 mr-2" />
                                Exportar
                              </DropdownMenuItem>
                            )}
                            
                            {/* Ações de edição - requerem permissão */}
                            {canEdit && (
                              <>
                                <DropdownMenuItem onClick={() => onEditName(flow)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Editar nome
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onDuplicate(flow)}>
                                  <Copy className="h-4 w-4 mr-2" />
                                  Duplicar
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            {/* Excluir - requer permissão delete */}
                            {canDelete && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => onDeleteFlow(flow)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
